/**
 * Cliente HTTP para el endpoint público del widget.
 * NO usa API_KEY — el backend permite anónimo (con CORS allowlist).
 */

export type Chain = 'ethereum' | 'bitcoin' | 'lightning';

export interface CapturePayload {
  /**
   * REQUERIDO. Lpay provisiona el mailbox en kmail (`provisionForUser`) y usa
   * la address resultante como `users.email`. El widget NO genera emails:
   * la única identidad del user es el username.
   */
  username: string;
  /**
   * Cadena para la wallet de payout. Default server: 'ethereum'.
   * El host la pasa para BTC ('bitcoin') o sats ('lightning').
   */
  chain?: Chain;
  walletAddress?: string;
  /** Nombre legal — usado en KYC de Transak (si Lpay aún no lo tiene). */
  firstName?: string;
  /** Apellido legal — usado en KYC de Transak. */
  lastName?: string;
  /** Teléfono E.164, ej. "+18495551234". El server lo rechaza si no matchea ^\+[1-9]\d{1,14}$. */
  phoneE164?: string;
  /** Fecha de nacimiento ISO YYYY-MM-DD. Debe ser pasada y >= 1900-01-01. */
  dateOfBirth?: string;
  card: {
    pan: string;
    cvv: string;
    expMonth: number;
    expYear: number;
    cardholderName: string;
  };
  billing: {
    line1: string;
    line2?: string | null;
    city: string;
    state?: string | null;
    zip: string;
    country: string;
  };
}

export interface CaptureResult {
  id: string;
  userId: string;
  walletId: string | null;
  brand: string;
  last4: string;
  /** Email asignado al user en kmail (si fue provisionado). */
  mailAddress?: string | null;
  /** true si el endpoint detectó duplicate y devolvió la card existente. */
  idempotent?: boolean;
}

export class WidgetApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.name = 'WidgetApiError';
  }
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown };
}

export const captureCard = async (
  apiBase: string,
  payload: CapturePayload,
): Promise<CaptureResult> => {
  let res: Response;
  try {
    res = await fetch(`${apiBase}/widget/cards`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new WidgetApiError(0, 'NETWORK_ERROR', 'No se pudo conectar al servidor.');
  }

  let body: { data?: CaptureResult } & ErrorEnvelope;
  try {
    body = (await res.json()) as { data?: CaptureResult } & ErrorEnvelope;
  } catch {
    body = {};
  }

  if (!res.ok) {
    const err = body.error;
    throw new WidgetApiError(
      res.status,
      err?.code ?? 'HTTP_ERROR',
      err?.message ?? `HTTP ${res.status}`,
      err?.details,
    );
  }

  if (!body.data) {
    throw new WidgetApiError(500, 'EMPTY_RESPONSE', 'Respuesta vacía del servidor.');
  }
  return body.data;
};
